/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/rules-of-hooks */
import { FC, ReactNode, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { map, take, tap } from 'rxjs';
import equal from 'fast-deep-equal/react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ContentCopy } from '@mui/icons-material';

import { Table } from '../../../../../../../../shared/components/table/table.component';
import {
  Popup,
  TPopupComponent,
} from '../../../../../../../../shared/components/popup/popup.abstract';
import { TPopupDefaultActions } from '../../../../../../../../shared/components/popup/services/popup.service';
import {
  Column,
  Footer,
  SFooter,
} from '../../../../../../../../shared/components/table/components/common.components';
import { storageHelper } from '../../../../../../../../shared/helpers/storage';
import { FieldsRow } from '../../../../../../../../shared/components/form';
import { CrossTableEditPopup } from './crossTableEdit.popup';
import {
  defaultCrosstableConfigurationDataRes,
  initCrossTableSettings,
} from '../../generalTab.state';
import { responseHandler } from '../../../../../../../../shared/responseHandler/responseHandler.ts';
import {
  CrosstableConfigurationDataRes,
  EditPrintListGeneralTabRes,
  editPrintListService,
} from '../../../../../../services/editPrintList.service';
import { ISelectOption } from '../../../../../../../../shared/components/form/fields/select.component';
import {
  DictCustomerListsRes,
  DictCustomersRes,
  DictProductionPrintListsRes,
} from '../../../../../../../../shared/services/configsData/configsData.service';
import { useBeforeClosePopup } from '../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import { C_Order_Type } from '../../../../../../../../graphql/generatedModel';
import { getCustomerOrListId } from '../../utils/generalTab.utils';
import { CopyCrossTablePopup } from './copyCrossTable/copyCrossTable.popup.tsx';
import { isEnterKey } from '../../../../../../../../shared/helpers/utils/utils.helper.ts';

export class CrossTablePopup extends Popup<ICrossTablePopup> {
  Component: FC<TPopupComponent>;

  constructor() {
    super();
    const stream = this.innerStream;
    this.Component = () => {
      const Wrapper = useMemo(() => this.Wrapper, []);
      const { t } = useTranslation();
      const {
        getValues,
        reset,
        setValue,
        formState: { dirtyFields, isDirty, defaultValues },
      } = useFormContext();
      const crosstableConfigurationData =
        getValues()?.crosstableConfigurationData?.map(
          (setting: EditPrintListGeneralTabRes['crosstableConfigurationData'][number]) => ({
            ...setting,
            id: setting?.categoryId,
          }),
        ) || [];
      const [selectedRow, setSelectedRow] = useState<
        EditPrintListGeneralTabRes['crosstableConfigurationData'][number] | null
      >(null);
      const [values, setValues] = useState<ICrossTablePopup['values']>(undefined);
      const [loading, setLoading] = useState<boolean>(false);

      const crossTableEditPopup = useMemo(() => new CrossTableEditPopup(), []);
      const copyCrossTablePopup = useMemo(() => new CopyCrossTablePopup(), []);
      const handleApply = useRef(() => {});

      const onClose = () => {
        if (dirtyFields?.crosstableConfigurationData) reset();
      };
      const onApply = () => {
        if (
          !equal(
            getValues()?.crosstableConfigurationData,
            defaultValues?.crosstableConfigurationData,
          )
        ) {
          handleApply.current();
        }
        setSelectedRow(null);
        stream.emit('close');
      };

      const onAddNewCrosstableSetting = () => {
        const lastIndex = crosstableConfigurationData.length;
        const lastCategoryId =
          crosstableConfigurationData.reduce(
            (
              maxId: number,
              setting: EditPrintListGeneralTabRes['crosstableConfigurationData'][number],
            ) => Math.max(maxId, parseInt(setting.categoryId, 10)),
            0,
          ) + 1;

        const newSetting = {
          ...initCrossTableSettings,
          id: lastCategoryId.toString(),
          categoryId: lastCategoryId.toString(),
        };

        const updatedData = [...crosstableConfigurationData, newSetting];
        setValue('crosstableConfigurationData', updatedData, {
          shouldDirty: true,
        });

        crossTableEditPopup.stream.emit('open', {
          values: {
            row: {
              ...newSetting,
              index: lastIndex,
            },
            type: 'create',
            field: 'categoryName',
            customersOptions:
              values?.customersOptions.map(({ id, customerNo, internalOrFullName }) => ({
                id,
                label: `${customerNo} ${internalOrFullName}`,
                customerNo,
                internalOrFullName,
              })) || [],
            customerListsOptions: values?.customerListsOptions || [],
          },
        });
      };
      const onDeleteCrosstableSetting = () => {
        if (selectedRow) {
          const updatedData = crosstableConfigurationData.filter(
            (setting: EditPrintListGeneralTabRes['crosstableConfigurationData'][number]) =>
              setting.id !== selectedRow.id,
          );
          const currentIndex = crosstableConfigurationData.findIndex(
            (setting: EditPrintListGeneralTabRes['crosstableConfigurationData'][number]) =>
              setting.id === selectedRow.id,
          );

          let newSelectedRow = null;
          if (currentIndex > 0) {
            newSelectedRow = updatedData[currentIndex - 1];
          } else if (currentIndex === 0 && updatedData.length > 0) {
            newSelectedRow = updatedData[0];
          }
          setValue('crosstableConfigurationData', updatedData, {
            shouldDirty: true,
          });

          setSelectedRow(newSelectedRow);
        }
      };
      const onCopyPrintList = (id: string) => {
        setLoading(true);
        editPrintListService.sub
          .crosstableConfigurationData()
          .pipe(
            responseHandler<CrosstableConfigurationDataRes>({
              errorReturnType: { ...defaultCrosstableConfigurationDataRes },
            }),
            map(({ crosstableConfigurationData }) => {
              setValue('crosstableConfigurationData', crosstableConfigurationData, {
                shouldDirty: true,
              });
            }),
            tap(() => setLoading(false)),
            take(1),
          )
          .subscribe();
        editPrintListService.pub.crosstableConfigurationData({
          id,
        });
      };

      const buttonList: IButtonList[] = useMemo(
        () => [
          {
            title: t('article.addNewRecord'),
            icon: <AddIcon />,
            onClick: onAddNewCrosstableSetting,
            disabled: crosstableConfigurationData.length > 52,
          },
          {
            title: t('article.deleteRecord'),
            icon: <RemoveIcon />,
            onClick: onDeleteCrosstableSetting,
            disabled: !crosstableConfigurationData.length || !selectedRow,
          },
          {
            title: t('production.copy'),
            icon: <ContentCopy />,
            onClick: () =>
              copyCrossTablePopup.stream.emit('open', {
                printListsOptions: values?.printListsOptions as ISelectOption[],
              }),
          },
        ],
        [t, values, crosstableConfigurationData, selectedRow, values?.printListsOptions],
      );

      useBeforeClosePopup({
        stream,
        isDirty,
        handlers: () => {
          onClose();
          setSelectedRow(null);
        },
      });

      useEffect(() => {
        stream.actionListener('open').subscribe(({ onApply, values }) => {
          handleApply.current = onApply;
          if (values) setValues(values);
        });

        copyCrossTablePopup.stream.state().subscribe((v) => {
          const id = v?.idForCopy;
          if (v?.action === 'save' && id) {
            onCopyPrintList(id);
          }
        });

        return () => {
          copyCrossTablePopup.stream.unsubscribe();
        };
      }, []);

      return (
        <Wrapper
          stream={stream}
          popupTitle={t('production.cross_table_setup')}
          slotProps={{ paper: { sx: { maxWidth: '1200px', maxHeight: '700px' } } }}
          fullWidth
        >
          <Stack
            direction='column'
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <FieldsRow marginBottom={1} alignItems='center' spacing={0}>
              <Stack direction='row' spacing={2}>
                {buttonList.map(({ icon, ...props }, i) => (
                  <IconButton key={i} color='primary' size='large' {...props}>
                    {icon}
                  </IconButton>
                ))}
              </Stack>
            </FieldsRow>
            <STable
              data={crosstableConfigurationData?.map(
                (
                  setting: EditPrintListGeneralTabRes['crosstableConfigurationData'][number],
                  index: number,
                ) => ({
                  ...setting,
                  index,
                }),
              )}
              initialState={{
                columns: storageHelper.local.getItem('editPrintList.crossTableColumnModel'),
              }}
              onChangeColumnModel={(v) =>
                storageHelper.local.setItem('editPrintList.crossTableColumnModel', v)
              }
              rowSelectionModel={selectedRow?.id || undefined}
              onRowClick={(v, e) => {
                const targetElement = e.target as HTMLElement;
                const field =
                  targetElement.dataset?.field || targetElement.parentElement?.dataset?.field;
                setSelectedRow(v.row);
                crossTableEditPopup.stream.emit('open', {
                  values: {
                    row: v.row,
                    field,
                    type: 'edit',
                    customersOptions: values?.customersOptions.map(
                      ({ id, customerNo, internalOrFullName }) => ({
                        id,
                        label: `${customerNo} ${internalOrFullName}`,
                        customerNo,
                        internalOrFullName,
                      }),
                    ),
                    customerListsOptions: values?.customerListsOptions,
                  },
                });
              }}
              focusTabProps={{
                enabled: true,
                enabledFields: [
                  'categoryId',
                  'customerIndicator',
                  'categoryName',
                  'categoryCustomerOrListId',
                  'categoryFilterDeliveryType',
                  'categoryFilterSplitNo',
                ],
              }}
              onCellKeyDown={({ row, field }, event) => {
                if (isEnterKey(event)) {
                  crossTableEditPopup.stream.emit('open', {
                    values: {
                      row,
                      field,
                      type: 'edit',
                      customersOptions: values?.customersOptions.map(
                        ({ id, customerNo, internalOrFullName }) => ({
                          id,
                          label: `${customerNo} ${internalOrFullName}`,
                          customerNo,
                          internalOrFullName,
                        }),
                      ),
                      customerListsOptions: values?.customerListsOptions,
                    },
                  });
                }
              }}
              height={400}
              loading={loading}
            >
              <Column
                field='categoryName'
                headerName={t('production.group_name')}
                sortable={false}
                width={200}
              />
              <Column
                field='customerIndicator'
                headerName={t('production.filter_by')}
                sortable={false}
                width={200}
                renderCell={({ value, row }) => {
                  const categoryCustomerOrListId = getCustomerOrListId(
                    row,
                    values!.customersOptions,
                    values!.customerListsOptions,
                  );
                  const customerIndicator = Number(value)
                    ? t('common.customers_list')
                    : t('common.customer');
                  return (
                    <Box
                      sx={{
                        p: 1,
                      }}
                    >
                      {categoryCustomerOrListId ? customerIndicator : ''}
                    </Box>
                  );
                }}
              />
              <Column
                field='categoryCustomerOrListId'
                headerName={t('production.customer_or_customer_list')}
                sortable={false}
                width={400}
                renderCell={({ row }) => {
                  const id = getCustomerOrListId(
                    row,
                    values!.customersOptions,
                    values!.customerListsOptions,
                  );

                  return (
                    <Box
                      sx={{
                        p: 1,
                      }}
                    >
                      {id}
                    </Box>
                  );
                }}
              />
              <Column
                field='categoryFilterDeliveryType'
                headerName={t('production.order_type')}
                sortable={false}
                width={200}
                renderCell={({ value }) => {
                  const deliveryType = value
                    ? t(`enums.${C_Order_Type[value as keyof typeof C_Order_Type]}`)
                    : null;
                  return (
                    <Box
                      sx={{
                        p: 1,
                      }}
                    >
                      {deliveryType}
                    </Box>
                  );
                }}
              />
              <Column
                field='categoryFilterSplitNo'
                headerName={t('production.split_no')}
                valueGetter={(value) => (value === 'nothing' ? t('common.empty') : value)}
                sortable={false}
                flex={1}
              />
              <Footer>
                <SCustomFooter>{t('common.max')}: 51</SCustomFooter>
              </Footer>
            </STable>
          </Stack>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              mt: 3,
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={onApply} variant='contained' sx={{ width: 200 }}>
              {t('common.save')}
            </Button>
          </Stack>
          <crossTableEditPopup.Component />
          <copyCrossTablePopup.Component />
        </Wrapper>
      );
    };
  }
}

const STable = styled(Table)({
  '& .MuiDataGrid-cell': {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'inherit',
  },
});

const SCustomFooter = styled(SFooter)({
  justifyContent: 'end',
});
interface ICrossTablePopup {
  action: TPopupDefaultActions;
  onApply: () => void;
  values?: {
    printListsOptions: DictProductionPrintListsRes;
    customersOptions: DictCustomersRes;
    customerListsOptions: DictCustomerListsRes;
  };
}

interface IButtonList {
  title: string;
  icon: ReactNode;
  disabled?: boolean;
  active?: boolean;
  onClick?: () => void;
}
